<template>
  <div class="h-full flex flex-col">
    <div
      v-if="loading && alerts.length === 0"
      class="flex flex-col items-center justify-center gap-2 mt-20"
    >
      <CustomLoader :size="28" />
    </div>

    <div v-else-if="alerts.length > 0" class="h-full flex flex-col">
      <div
        ref="scrollContainer"
        class="flex-1 overflow-y-auto custom_scrollbar"
      >
        <div
          v-for="alert in alerts"
          :key="alert.id"
          class="p-2 flex w-full flex-row justify-between items-center cursor-pointer alert_card rounded-lg mb-1"
          @click="handleAlertClick(alert)"
        >
          <div class="flex flex-row justify-between items-center w-full">
            <div class="flex flex-row gap-2 items-center min-w-0 flex-1">
              <div class="relative flex-shrink-0">
                <ProfileIcon :size="32" :font="12" :data="alert.company" />
              </div>
              <a-tooltip :title="alert.customer_name" placement="top" color="var(--tooltip-bg)">
                <span class="font-semibold text-[13px] truncate max-w-[300px]">{{ alert.customer_name }}</span>
              </a-tooltip>
            </div>

            <div class="flex flex-col gap-1 items-end flex-shrink-0">
              <a-tooltip :title="alert.alert_type" placement="top" color="var(--tooltip-bg)">
                <span class="text-xs secondary_color truncate max-w-[200px]">{{ alert.alert_type }}</span>
              </a-tooltip>
              <PriorityTagProvider
                source="alert_stream_section"
                :text="alert.severity"
              />
            </div>
          </div>
        </div>

        <div ref="bottomElement" class="flex justify-center py-2">
          <CustomLoader v-if="loadingMore" :size="16" />
        </div>
      </div>
    </div>

    <EmptyScreen
      v-else
      source="alert_stream"
      text="No alerts found"
      padding="mt-20"
      css="size-16"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from "vue";
import { useRouter } from "vue-router";
import AlertService from "@/src/api/AlertService.js";
import CustomLoader from "@/src/components/CustomLoader.vue";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import ProfileIcon from "@/src/components/ProfileIcon.vue";
import PriorityTagProvider from "@/src/components/PriorityTagProvider.vue";
import { priorityOptionsForCard } from "@/src/utils/alertUtils.js";

const props = defineProps({
  cardName: { type: String, default: "" },
  priorityFilter: { type: String, default: null },
  ownerId: { type: [Number, String], default: null },
  alertAgentId: { type: String, default: null },
  statusFilter: { type: String, default: null },
  disableLoadMore: { type: Boolean, default: false },
});

const router = useRouter();

const alerts = ref([]);
const loading = ref(true);
const loadingMore = ref(false);
const searchQuery = ref("");
const localPriority = ref(props.priorityFilter);
const currentPage = ref(1);
const hasMore = ref(true);
const pageSize = 20;

const scrollContainer = ref(null);
const bottomElement = ref(null);
let observer = null;
let searchDebounceTimer = null;

const priorityOptions = priorityOptionsForCard;

const mapAlert = (alert) => ({
  id: alert.id,
  customer_name: alert.alert_name || "Unknown",
  alert_type: alert.alert_agent?.agent_name || "Alert",
  severity: alert.priority?.toLowerCase() || "medium",
  status: alert.status?.toLowerCase() || "open",
  alert_info: alert.alert_info || "",
  remedy: alert.suggested_remediation || "",
  triggered_on: alert.created_at,
  object_type: alert.object_type,
  object_id: alert.object_id?.id || alert.object_id,
  company: {
    name: alert.alert_name || "Unknown",
  },
});

const fetchAlerts = async (page = 1, append = false) => {
  try {
    if (page === 1) {
      loading.value = true;
    } else {
      loadingMore.value = true;
    }

    const response = await AlertService.listAlerts(
      page,
      pageSize,
      localPriority.value,
      searchQuery.value || null,
      props.ownerId,
      props.alertAgentId,
      props.statusFilter,
    );

    if (response?.success) {
      const rawAlerts = response.alerts || [];
      const mappedAlerts = rawAlerts.map(mapAlert);
      
      if (append) {
        alerts.value = [...alerts.value, ...mappedAlerts];
      } else {
        alerts.value = mappedAlerts;
      }

      hasMore.value = props.disableLoadMore ? false : rawAlerts.length === pageSize;
      currentPage.value = page;
    }
  } catch (error) {
    console.error("Error fetching alerts:", error);
  } finally {
    loading.value = false;
    loadingMore.value = false;
  }
};

const loadMore = () => {
  if (!loadingMore.value && hasMore.value) {
    fetchAlerts(currentPage.value + 1, true);
  }
};

const handleSearchChange = (value) => {
  if (searchDebounceTimer) {
    clearTimeout(searchDebounceTimer);
  }
  
  searchDebounceTimer = setTimeout(() => {
    searchQuery.value = value;
    currentPage.value = 1;
    hasMore.value = true;
    fetchAlerts(1, false);
  }, 300);
};

const selectPriority = (value) => {
  localPriority.value = value;
  currentPage.value = 1;
  hasMore.value = true;
  fetchAlerts(1, false);
};

const clearPriority = () => {
  localPriority.value = null;
  currentPage.value = 1;
  hasMore.value = true;
  fetchAlerts(1, false);
};

const handleAlertClick = (alert) => {
  router.push({
    name: "customer-success-alerts",
    hash: "#stream",
    query: { alertId: alert.id },
  });
};

const setupIntersectionObserver = () => {
  if (props.disableLoadMore) return;
  if (!bottomElement.value || !scrollContainer.value) return;

  if (observer) {
    observer.disconnect();
  }

  observer = new IntersectionObserver(
    (entries) => {
      const [entry] = entries;
      if (entry.isIntersecting && !loadingMore.value && hasMore.value) {
        loadMore();
      }
    },
    {
      root: scrollContainer.value,
      rootMargin: "50px",
      threshold: 0.1,
    },
  );

  observer.observe(bottomElement.value);
};

watch(
  () => props.priorityFilter,
  (newVal) => {
    localPriority.value = newVal;
    fetchAlerts(1, false);
  },
);

watch(
  () => [props.ownerId, props.alertAgentId, props.statusFilter],
  () => {
    currentPage.value = 1;
    hasMore.value = true;
    fetchAlerts(1, false);
  },
);

watch(
  () => alerts.value,
  () => {
    if (alerts.value.length > 0 && scrollContainer.value && bottomElement.value) {
      setupIntersectionObserver();
    }
  },
  { flush: "post" },
);

onMounted(() => {
  fetchAlerts();
});

onUnmounted(() => {
  if (observer) {
    observer.disconnect();
    observer = null;
  }
  if (searchDebounceTimer) {
    clearTimeout(searchDebounceTimer);
  }
});
</script>

<style scoped>
.border_bottom {
  border-bottom: 1px solid var(--white-opacity-8);
}

.priority_trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 6px;
  border: 1px solid var(--white-opacity-8);
  background-color: var(--white-opacity-4);
  cursor: pointer;
  transition: all 0.2s;
  min-width: 100px;
  white-space: nowrap;
}

.priority_trigger:hover {
  background-color: var(--white-opacity-8);
  border-color: var(--white-opacity-12);
}

.priority_options {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 120px;
}

.priority_option {
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.priority_option:hover {
  background-color: var(--white-opacity-8);
}

.priority_option.selected {
  background-color: var(--white-opacity-12);
}

.priority_option.clear_option {
  border-top: 1px solid var(--white-opacity-8);
  margin-top: 4px;
  padding-top: 12px;
}

.alert_card {
  transition: background-color 0.2s;
}

.alert_card:hover {
  background-color: var(--white-opacity-4);
}

.custom_scrollbar {
  scrollbar-width: thin;
  scrollbar-color: var(--white-opacity-12) transparent;
}

.custom_scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom_scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.custom_scrollbar::-webkit-scrollbar-thumb {
  background-color: var(--white-opacity-12);
  border-radius: 3px;
}

.custom_scrollbar::-webkit-scrollbar-thumb:hover {
  background-color: var(--white-opacity-16);
}
</style>
